// src/components/ForgotPassword.jsx
import React, { useEffect, useMemo, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { apiFetch } from '../api'

function KirassMark({ className = '' }) {
  return (
    <svg viewBox="0 0 48 48" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M10 24L24 10h10L20 24l14 14H24L10 24z" fill="currentColor" opacity="0.95" />
      <path d="M22 24L34 12h4L26 24l12 12h-4L22 24z" fill="currentColor" opacity="0.55" />
    </svg>
  )
}

function IconMail(props) {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="none" aria-hidden="true" {...props}>
      <path d="M4 6h16v12H4V6Z" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
      <path d="M4.5 7l7.5 6 7.5-6" stroke="currentColor" strokeWidth="1.8" strokeLinejoin="round" />
    </svg>
  )
}

export default function ForgotPassword({
  tenantName,
  productName = 'Votre structure',
  platformBrand = 'KIRASS GROUP',
  adminEmail = 'azdinekirasse1@gmail.com',
  madeBy = 'AKGroup UB',
  signatureLabel = 'Conception & intégration',
}) {
  const navigate = useNavigate()

  // ✅ aligné backend/auth.js
  const FORGOT_ENDPOINT = '/api/auth/forgot-password'

  const [email, setEmail] = useState('')
  const [loading, setLoading] = useState(false)
  const [sent, setSent] = useState(false)
  const [error, setError] = useState('')
  const [info, setInfo] = useState('')

  const inputRef = useRef(null)

  const orgName = (tenantName || productName || 'Votre structure').trim()
  const year = new Date().getFullYear()

  useEffect(() => {
    setTimeout(() => inputRef.current?.focus(), 80)
  }, [])

  const adminMailto = useMemo(() => {
    const subject = `[${orgName}] Mot de passe oublié`
    const body =
      `Bonjour,\n\n` +
      `Je n’arrive pas à réinitialiser mon mot de passe sur: ${orgName}\n\n` +
      `Email: ${email || '(non renseigné)'}\n` +
      `Merci.\n`
    return `mailto:${adminEmail}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(body)}`
  }, [adminEmail, orgName, email])

  const CardShell =
    'h-full rounded-[28px] border border-slate-200 bg-white/75 shadow-[0_25px_90px_-60px_rgba(15,23,42,.20)] backdrop-blur-xl'

  async function handleSubmit(e) {
    e.preventDefault()
    if (loading) return

    setError('')
    setInfo('')

    const v = email.trim()
    if (!v) {
      setError('Saisis ton email.')
      setTimeout(() => inputRef.current?.focus(), 60)
      return
    }

    setLoading(true)
    try {
      // ✅ ton backend répond toujours OK si compte inexistant (anti-enum)
      await apiFetch(FORGOT_ENDPOINT, { method: 'POST', body: { email: v } })
      setSent(true)
      setInfo("Si un compte existe avec cet email, un lien de réinitialisation a été envoyé.")
    } catch (err) {
      console.error('Forgot error:', err)
      // ✅ même message (anti-enum)
      setSent(true)
      setInfo("Si un compte existe avec cet email, un lien de réinitialisation a été envoyé.")
    } finally {
      setLoading(false)
    }
  }

  return (
    <div className="min-h-screen text-slate-900">
      {/* Background (identique Auth.jsx) */}
      <div className="fixed inset-0">
        <div className="absolute inset-0 bg-gradient-to-br from-slate-50 via-white to-slate-100" />
        <div className="absolute -top-40 left-[-10%] h-[520px] w-[520px] rounded-full bg-sky-300/50 blur-3xl" />
        <div className="absolute top-10 right-[-15%] h-[560px] w-[560px] rounded-full bg-slate-300/40 blur-3xl" />
        <div className="absolute bottom-[-20%] left-[35%] h-[520px] w-[520px] rounded-full bg-emerald-300/35 blur-3xl" />
        <div className="absolute inset-0 opacity-[0.18] bg-[linear-gradient(to_right,rgba(15,23,42,.09)_1px,transparent_1px),linear-gradient(to_bottom,rgba(15,23,42,.09)_1px,transparent_1px)] bg-[size:56px_56px]" />
      </div>

      <div className="relative mx-auto flex min-h-screen max-w-6xl items-stretch px-4 py-10">
        <div className="grid w-full grid-cols-1 gap-8 lg:grid-cols-12 lg:gap-8 items-stretch">
          {/* LEFT (desktop) */}
          <div className={`relative hidden lg:flex lg:col-span-6 ${CardShell} p-8 flex-col justify-between min-h-[560px]`}>
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-11 w-11 items-center justify-center rounded-2xl bg-slate-900 text-white shadow-sm">
                  <KirassMark className="h-7 w-7 text-white" />
                </div>
                <div className="leading-tight">
                  <div className="text-[11px] tracking-[0.22em] text-slate-500">{platformBrand}</div>
                  <div className="text-lg font-semibold text-slate-900">{orgName}</div>
                </div>
              </div>

              <div className="mt-12 max-w-sm">
                <div className="text-3xl font-semibold tracking-tight text-slate-900">Mot de passe oublié</div>
                <p className="mt-3 text-sm leading-relaxed text-slate-600">
                  Renseigne ton email. Si un compte existe, tu recevras un lien de réinitialisation.
                  En cas de souci, contacte l’admin.
                </p>

                <a
                  href={adminMailto}
                  className="mt-7 inline-flex w-full items-center justify-center gap-2 rounded-2xl bg-slate-900 px-4 py-3 text-sm font-medium text-white shadow-[0_18px_60px_-40px_rgba(15,23,42,.35)] transition hover:bg-slate-800"
                  title="Ouvrir la messagerie (mail pré-rempli)"
                >
                  <IconMail className="opacity-90" />
                  Contacter l’admin
                </a>
              </div>
            </div>

            <div>
              <div className="mt-6 flex items-center justify-between text-xs text-slate-500">
                <span>© {year} {orgName}</span>
                <span className="tracking-[0.18em] text-slate-400">Propulsé par {platformBrand}</span>
              </div>
              <div className="pointer-events-none absolute bottom-6 left-6 flex items-center gap-2 opacity-[0.06]">
                <KirassMark className="h-5 w-5 text-slate-900" />
                <span className="text-xs tracking-[0.24em] text-slate-900">{platformBrand}</span>
              </div>
            </div>
          </div>

          {/* RIGHT */}
          <div className="flex h-full items-stretch justify-center lg:col-span-6">
            <div className="w-full max-w-lg h-full flex flex-col">
              <div className="mb-4 flex items-center justify-between">
                <div className="flex items-center gap-2 rounded-full border border-slate-200 bg-white/70 px-3 py-1.5 shadow-sm">
                  <KirassMark className="h-4 w-4 text-slate-900" />
                  <span className="text-[11px] tracking-[0.22em] text-slate-600">{platformBrand}</span>
                </div>
                <div className="text-xs text-slate-500">© {year}</div>
              </div>

              <div className={`${CardShell} min-h-[560px] flex flex-col overflow-hidden`}>
                <div className="px-7 pt-7">
                  <div className="text-[11px] font-semibold tracking-[0.22em] text-slate-500">{orgName}</div>
                  <div className="mt-1 text-2xl font-semibold tracking-tight text-slate-900">Récupération</div>
                  <div className="mt-1 text-sm text-slate-600">
                    {sent ? 'Vérifie ta boîte mail.' : 'On envoie un lien si le compte existe.'}
                  </div>
                </div>

                <div className="px-7 pb-7 pt-6 flex-1">
                  {error && (
                    <div className="mb-4 rounded-2xl border border-red-200 bg-red-50 p-3 text-sm text-red-700">
                      {error}
                    </div>
                  )}
                  {info && (
                    <div className="mb-4 rounded-2xl border border-slate-200 bg-slate-50 p-3 text-sm text-slate-800">
                      {info}
                    </div>
                  )}

                  {sent ? (
                    <div className="space-y-4">
                      <div className="rounded-2xl border border-emerald-200 bg-emerald-50 p-4">
                        <div className="font-semibold text-emerald-900">Demande prise en compte</div>
                        <div className="text-sm text-emerald-800 mt-1">Pense aussi aux spams.</div>
                      </div>

                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => navigate('/auth')}
                          className="text-sm font-medium text-slate-700 hover:text-slate-900"
                        >
                          ← Retour à la connexion
                        </button>

                        <a
                          href={adminMailto}
                          className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
                          title="Contacter l’admin"
                        >
                          <IconMail />
                          Contacter l’admin
                        </a>
                      </div>
                    </div>
                  ) : (
                    <form onSubmit={handleSubmit} className="space-y-4">
                      <div>
                        <label className="text-sm font-medium text-slate-800">Email</label>
                        <div className="mt-1.5">
                          <input
                            ref={inputRef}
                            type="email"
                            value={email}
                            onChange={(e) => { setEmail(e.target.value); if (error || info) { setError(''); setInfo('') } }}
                            disabled={loading}
                            placeholder="ex: nom@domaine.com"
                            autoComplete="email"
                            className="w-full rounded-2xl border border-slate-200 bg-white px-4 py-3 text-slate-900 placeholder:text-slate-400
                                       outline-none transition focus:border-slate-300 focus:ring-4 focus:ring-slate-200/60"
                          />
                        </div>
                        <div className="mt-2 text-xs text-slate-500">
                          Pour la sécurité, tu verras toujours le même message.
                        </div>
                      </div>

                      <button
                        type="submit"
                        disabled={loading || !email.trim()}
                        className={`w-full rounded-2xl px-4 py-3 text-white font-medium transition
                          ${loading || !email.trim()
                            ? 'bg-slate-300 cursor-not-allowed'
                            : 'bg-slate-900 hover:bg-slate-950 shadow-[0_18px_60px_-40px_rgba(79,70,229,.55)]'
                          }`}
                      >
                        {loading ? 'Envoi…' : 'Envoyer le lien'}
                      </button>

                      <div className="flex items-center justify-between">
                        <button
                          type="button"
                          onClick={() => navigate('/auth')}
                          className="text-sm font-medium text-slate-700 hover:text-slate-900"
                        >
                          ← Retour à la connexion
                        </button>

                        <a
                          href={adminMailto}
                          className="inline-flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900"
                          title="Contacter l’admin"
                        >
                          <IconMail />
                          Contacter l’admin
                        </a>
                      </div>
                    </form>
                  )}
                </div>

                <div className="px-7 pb-7">
                  <div className="pt-2 space-y-2">
                    <div className="text-[11px] text-center text-slate-500 leading-relaxed">
                      Astuce: utilise une adresse email que tu consultes souvent.
                    </div>
                    <div className="text-[11px] text-center text-slate-500">
                      <span>{signatureLabel}</span>{' '}
                      <span className="text-slate-300">•</span>{' '}
                      <span className="font-medium text-slate-700">{madeBy}</span>
                    </div>
                  </div>
                </div>
              </div>

              <div className="mt-6 lg:hidden">
                <a
                  href={adminMailto}
                  className="inline-flex w-full items-center justify-center gap-2 rounded-2xl border border-slate-200 bg-white/80 px-4 py-3 text-sm font-medium text-slate-900 hover:bg-white transition"
                >
                  <IconMail />
                  Contacter l’admin
                </a>
              </div>
            </div>
          </div>

          <div className="hidden lg:block lg:col-span-12">
            <div className="mt-2 text-center text-xs text-slate-500">
              <span className="opacity-80">Propulsé par</span>{' '}
              <span className="tracking-[0.22em]">{platformBrand}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
